import { useState, useRef } from 'react';
import { QRCodeCanvas } from 'qrcode.react';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogTrigger } from '@/components/ui/dialog';
import { QrCode, Printer, Download } from 'lucide-react';
import { useLanguage } from '@/contexts/LanguageContext';

/**
 * Printable QR poster for a property listing.
 *
 * The QR encodes the canonical share URL (`/api/share/property/{id}`),
 * which serves OG-rich HTML AND redirects human visitors to the SPA
 * detail page. The poster also prints the WakaCode in plain text so a
 * buyer can paste it manually if they can't scan.
 */
export default function PropertyQRPoster({ property, triggerSize = 'sm', triggerVariant = 'outline' }) {
  const { language } = useLanguage();
  const [open, setOpen] = useState(false);
  const printableRef = useRef(null);
  const tt = (en, es) => (language === 'es' ? es : en);

  if (!property?.id) return null;

  const shareUrl = `${process.env.REACT_APP_BACKEND_URL}/api/share/property/${property.id}`;
  const wakacode = property.wakacode || '';
  const title = property.title || '';
  const cityLine = [property.city, property.neighborhood].filter(Boolean).join(', ');

  const handlePrint = () => {
    const node = printableRef.current;
    if (!node) return;
    const html = node.innerHTML;
    const w = window.open('', 'wakacasa-qr-print', 'width=720,height=900');
    if (!w) return;
    w.document.write(`<!doctype html><html><head><title>WakaCasa · ${wakacode || title}</title>
      <style>
        @page { size: A4 portrait; margin: 18mm; }
        * { box-sizing: border-box; -webkit-print-color-adjust: exact; print-color-adjust: exact; }
        body { font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Helvetica, Arial, sans-serif; color: #0B192C; margin: 0; padding: 0; }
        .poster { background: #FFFBF0; border: 4px solid #0B192C; border-radius: 18px; padding: 36px; max-width: 600px; margin: 0 auto; text-align: center; }
        .brand { color: #D4AF37; font-weight: 800; letter-spacing: 4px; font-size: 18px; text-transform: uppercase; }
        .tagline { color: #0B192C; opacity: 0.7; font-size: 13px; margin-top: 4px; letter-spacing: 1px; }
        h1 { font-size: 28px; line-height: 1.2; margin: 26px 0 6px; font-weight: 800; }
        .city { font-size: 14px; color: #475569; margin-bottom: 24px; }
        .qr-box { background: #fff; padding: 22px; display: inline-block; border-radius: 14px; border: 2px solid #D4AF37; }
        .codes { margin-top: 22px; }
        .wakacode { font-family: "Courier New", monospace; font-size: 28px; font-weight: 700; letter-spacing: 4px; color: #0B192C; background: #D4AF37; padding: 10px 18px; border-radius: 10px; display: inline-block; }
        .reference { font-family: "Courier New", monospace; font-size: 13px; color: #475569; margin-top: 8px; }
        .footer { margin-top: 26px; font-size: 11px; color: #475569; line-height: 1.5; }
        .footer strong { color: #0B192C; }
      </style></head><body>${html}</body></html>`);
    w.document.close();
    w.focus();
    setTimeout(() => { w.print(); }, 350);
  };

  const handleDownload = () => {
    const canvas = printableRef.current?.querySelector('canvas');
    if (!canvas) return;
    const url = canvas.toDataURL('image/png');
    const a = document.createElement('a');
    a.href = url;
    a.download = `wakacasa-${wakacode || property.id}.png`;
    a.click();
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button
          size={triggerSize}
          variant={triggerVariant}
          className="gap-1.5 border-[#D4AF37] text-[#0B192C] hover:bg-[#D4AF37]/10"
          data-testid="qr-poster-trigger"
        >
          <QrCode className="w-4 h-4" />
          {tt('QR Poster', 'Póster QR')}
        </Button>
      </DialogTrigger>
      <DialogContent className="max-w-md" data-testid="qr-poster-dialog">
        <div className="space-y-3">
          {/* Printable poster — also rendered on screen as a preview */}
          <div ref={printableRef}>
            <div className="poster" style={{
              background: '#FFFBF0', border: '4px solid #0B192C', borderRadius: 18,
              padding: '28px 22px', textAlign: 'center'
            }}>
              <div className="brand" style={{ color: '#D4AF37', fontWeight: 800, letterSpacing: 4, fontSize: 16, textTransform: 'uppercase' }}>WakaCasa</div>
              <div className="tagline" style={{ color: '#0B192C', opacity: 0.7, fontSize: 12, marginTop: 4, letterSpacing: 1 }}>
                {tt('Buy with Confidence', 'Compra con Confianza')}
              </div>
              <h1 style={{ fontSize: 22, lineHeight: 1.2, margin: '20px 0 4px', fontWeight: 800, color: '#0B192C' }} data-testid="qr-poster-title">{title}</h1>
              {cityLine && <div className="city" style={{ fontSize: 13, color: '#475569', marginBottom: 18 }}>{cityLine}</div>}

              <div className="qr-box" style={{ background: '#fff', padding: 18, display: 'inline-block', borderRadius: 12, border: '2px solid #D4AF37' }}>
                <QRCodeCanvas
                  value={shareUrl}
                  size={200}
                  level="M"
                  marginSize={2}
                  fgColor="#0B192C"
                  bgColor="#ffffff"
                  data-testid="qr-poster-canvas"
                />
              </div>

              <div className="codes" style={{ marginTop: 18 }}>
                {wakacode && (
                  <div className="wakacode" style={{
                    fontFamily: '"Courier New", monospace', fontSize: 22, fontWeight: 700,
                    letterSpacing: 4, color: '#0B192C', background: '#D4AF37',
                    padding: '8px 14px', borderRadius: 8, display: 'inline-block'
                  }}
                    data-testid="qr-poster-wakacode">
                    {wakacode}
                  </div>
                )}
                {property.reference && (
                  <div className="reference" style={{ fontFamily: '"Courier New", monospace', fontSize: 12, color: '#475569', marginTop: 6 }}>
                    {tt('Ref:', 'Ref:')} {property.reference}
                  </div>
                )}
              </div>

              <div className="footer" style={{ marginTop: 20, fontSize: 10, color: '#475569', lineHeight: 1.5 }}>
                <div>{tt('Scan the QR or visit', 'Escanea el QR o visita')} <strong style={{ color: '#0B192C' }}>WakaCasa.com</strong></div>
                {wakacode && (
                  <div>{tt('and paste code', 'y pega el código')} <strong style={{ color: '#0B192C' }}>{wakacode}</strong></div>
                )}
              </div>
            </div>
          </div>

          <div className="flex gap-2">
            <Button onClick={handlePrint} className="flex-1 bg-[#0B192C] hover:bg-[#0B192C]/90 text-white gap-1.5" data-testid="qr-poster-print">
              <Printer className="w-4 h-4" />
              {tt('Print Poster', 'Imprimir Póster')}
            </Button>
            <Button onClick={handleDownload} variant="outline" className="gap-1.5" data-testid="qr-poster-download">
              <Download className="w-4 h-4" />
              {tt('PNG', 'PNG')}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
